import { ImageUser } from '@kula-ai/icons/dual-tone/ImageUser';
import Google from '@/icons/Google';
import { Microsoft } from '@/assets/icons/mailbox';
import * as Yup from 'yup';
import { Okta, Jumpcloud } from '@/assets/icons/sso';
import { RipplingIcon } from '@/assets/icons/HRIS';
import { Key01 } from '@kula-ai/icons/dual-tone/Key01';

interface SignInConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType;
  isActive: boolean;
}

interface SSOOption {
  label: string;
  id: string;
  icon?: React.ComponentType;
}

interface SSOConfig {
  name: string;
  label: string;
  isRequired: boolean;
  placeholder: string;
  options?: SSOOption[];
  valueKey?: string;
  errorMessage?: string;
  isMulti?: boolean;
  isAppendToBody?: boolean;
  type: string;
  isConditional?: boolean;
  validation?: any;
  maxLength?: number;
  customFormatLabel?: React.ComponentType;
  conditionConfig?: {
    field: string;
    type: string;
    customFunc: (arg1: any, allValues: any) => boolean;
  };
}

interface FieldConfig {
  label: string;
  value: string;
  placeholder: string;
}

interface AttributeMapping {
  name: string;
  value: string;
}

export const standardSignInConfigs: SignInConfig[] = [
  {
    id: 'username_login',
    name: 'Username and Login',
    description:
      'Allow employees to login with their own username and password',
    icon: ImageUser,
    isActive: true,
  },
  {
    id: 'google',
    name: 'Google',
    description: 'Allow employees to login with their google account',
    icon: Google,
    isActive: true,
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    description: 'Allow employees to login with their microsoft account',
    icon: Microsoft,
    isActive: true,
  },
];

export const ssoConfigs: SignInConfig[] = [
  {
    id: 'google',
    name: 'Google',
    description: 'Allow employees to login with their google account',
    icon: Google,
    isActive: true,
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    description: 'Allow employees to login with their microsoft account',
    icon: Microsoft,
    isActive: true,
  },
];

export const spSettingsFields: FieldConfig[] = [
  {
    label: 'Assertion Consumer Service (ACS) URL',
    value: 'https://api.kula.ai/api/v1/auth/saml/callback',
    placeholder: 'Add your Kula email',
  },
  {
    label: 'Service Provider (SP) Entity ID',
    value: 'https://api.kula.ai/api/v1/auth/saml/metadata',
    placeholder: 'Add your Kula email',
  },
];

export const attributes: AttributeMapping[] = [
  { name: 'email', value: 'user.email' },
  { name: 'first_name', value: 'user.firstName' },
  { name: 'last_name', value: 'user.lastName' },
];

export const tabBarOptions: SSOOption[] = [
  { label: 'Manually enter them', id: 'mannual' },
  { label: 'Enter metadata URL', id: 'metadata-url' },
  { label: 'Upload XML', id: 'upload-metadata' },
];

export const mannualIDPConfigs: SSOConfig[] = [
  {
    name: 'sso_url',
    label: 'SSO URL',
    placeholder: 'https://your-idp.com/sso/saml',
    isRequired: true,
    type: 'input',
    maxLength: 250,
    validation: Yup.string()
      .matches(
        /^(https?:\/\/[^\s.]+\.\S{2,}(\/\S*)?|mailto:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?)$/i,
        'Please enter a valid SSO URL'
      )
      .required('SSO URL is required'),
  },
  {
    name: 'entity_id',
    label: 'Entity ID',
    placeholder: 'https://your-idp.com/saml/entity',
    isRequired: true,
    type: 'input',
    maxLength: 250,
  },
  {
    name: 'certificate',
    label: 'X.509 Certificate',
    placeholder: 'Enter your certificate',
    isRequired: true,
    type: 'input',
  },
];

export const metadataURLConfigs: SSOConfig[] = [
  {
    name: 'metadata_url',
    label: 'Metadata URL',
    placeholder: 'https://your-idp.com/saml/metadata',
    isRequired: true,
    type: 'input',
    maxLength: 250,
    validation: Yup.string()
      .matches(
        /^(https?:\/\/[^\s.]+\.\S{2,}(\/\S*)?|mailto:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?)$/i,
        'Please enter a valid metadata URL'
      )
      .required('Metadata URL is required'),
  },
];

export const scimFields: FieldConfig[] = [
  {
    label: 'SCIM connector base URL',
    value: 'https://avatar.example.com/api/v1/scim',
    placeholder: 'Add your Kula email',
  },
  {
    label: 'Authorization token',
    value: 'df8fh229j199df9nd9nd9d9dd9j9d99dm0dm',
    placeholder: 'Add your Kula email',
  },
];

export const ssoAttributeMappings: Record<string, AttributeMapping[]> = {
  okta: [
    { name: 'email', value: 'user.email' },
    { name: 'first_name', value: 'user.firstName' },
    { name: 'last_name', value: 'user.lastName' },
  ],
  jump_cloud: [
    { name: 'email', value: 'email' },
    { name: 'first_name', value: 'firstName' },
    { name: 'last_name', value: 'lastName' },
  ],
  onelogin: [
    { name: 'email', value: 'onelogin.email' },
    { name: 'first_name', value: 'onelogin.firstname' },
    { name: 'last_name', value: 'onelogin.lastname' },
  ],
  auth0: [
    { name: 'email', value: 'auth0.email' },
    { name: 'first_name', value: 'auth0.given_name' },
    { name: 'last_name', value: 'auth0.family_name' },
  ],
  azure_ad: [
    { name: 'email', value: 'userPrincipalName' },
    { name: 'first_name', value: 'givenName' },
    { name: 'last_name', value: 'surname' },
  ],
  adfs: [
    {
      name: 'email',
      value:
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    },
    {
      name: 'first_name',
      value: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
    },
    {
      name: 'last_name',
      value: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
    },
  ],
  shibboleth: [
    { name: 'email', value: 'urn:oid:0.9.2342.19200300.100.1.3' },
    { name: 'first_name', value: 'urn:oid:2.5.4.42' },
    { name: 'last_name', value: 'urn:oid:2.5.4.4' },
  ],
  custom: [
    { name: 'email', value: 'user.email' },
    { name: 'first_name', value: 'user.firstName' },
    { name: 'last_name', value: 'user.lastName' },
  ],
};

export const iconMappings = [
  {
    integration_type: 'okta',
    icon: Okta,
  },
  {
    integration_type: 'rippling/rippling_api',
    icon: RipplingIcon,
  },
  {
    integration_type: 'jump_cloud',
    icon: Jumpcloud,
  },
  {
    integration_type: 'custom',
    icon: Key01,
  },
];
